import React, { useState } from "react";
import {
  Plus, Minus, Equal, ChevronDown, ArrowDown, ArrowLeftRight, Info
} from "lucide-react";
import { Badge } from "../ui/badge";

export function VistaMargini({ 
  layout, costiPerCategoria, costiOriginaliPerCategoria, ricaviPerCategoria, 
  columnKeys, formatCurrency, columnMode, costiViewMode, setCostiViewMode, ripartizioneInfo,
  dettaglioMap, columns, onCellClick,
  showCostoOrario = false, getOreColonna = () => 0, oreTotaliPeriodo = 0
}) {
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  if (!layout || !layout.layout || layout.layout.length === 0) return null;

  const rows = layout.layout;

  // Scegli la fonte costi in base al toggle (solo in modalità CdC)
  const useCostiOriginali = columnMode === "cdc" && costiViewMode === "originali" 
    && costiOriginaliPerCategoria && Object.keys(costiOriginaliPerCategoria).length > 0;
  const costiAttivi = useCostiOriginali ? costiOriginaliPerCategoria : costiPerCategoria;

  const allConti = [];
  for (const catData of Object.values(ricaviPerCategoria || {})) {
    for (const conto of Object.values(catData.conti || {})) {
      allConti.push({ ...conto, tipo: "ricavo" });
    }
  }
  for (const catData of Object.values(costiAttivi || {})) {
    for (const conto of Object.values(catData.conti || {})) {
      allConti.push({ ...conto, tipo: "costo" });
    }
  }

  const computedRows = [];
  const runningTotal = {};

  for (const row of rows) {
    if (row.tipo === "ricavi" || row.tipo === "gruppo") {
      const codici = row.codici || [];
      const categorieIds = row.categoria_ids || [];
      const categorieCodici = row.categoria_codici || [];
      const matchingConti = allConti.filter(c => {
        const cod = c.codice || "";
        // Match if conto code startsWith any prefix, OR if conto belongs to any selected categoria
        const matchCodice = codici.some(prefix => cod.startsWith(prefix));
        const matchCategoria = categorieIds.length > 0 && categorieIds.includes(c.categoria_id);
        const matchCategoriaCod = categorieCodici.length > 0 && categorieCodici.includes(c.categoria_codice);
        return matchCodice || matchCategoria || matchCategoriaCod;
      });

      const groupValues = {};
      let groupTotal = 0;
      for (const key of columnKeys) {
        const val = matchingConti.reduce((sum, c) => sum + (c.values[key] || 0), 0);
        groupValues[key] = val;
        groupTotal += val;
      }

      const segno = row.tipo === "ricavi" ? 1 : -1;
      for (const key of columnKeys) {
        runningTotal[key] = (runningTotal[key] || 0) + (groupValues[key] * segno);
      }

      computedRows.push({ ...row, values: groupValues, total: groupTotal, conti: matchingConti, segno });
    } else if (row.tipo === "margine") {
      const margineValues = { ...runningTotal };
      const margineTotal = columnKeys.reduce((s, k) => s + (margineValues[k] || 0), 0);
      computedRows.push({ ...row, values: margineValues, total: margineTotal });
    }
  }

  const toggleGroup = (idx) => {
    const s = new Set(expandedGroups);
    s.has(idx) ? s.delete(idx) : s.add(idx);
    setExpandedGroups(s);
  };

  // "Ricavi netti" = primo gruppo di tipo "ricavi" nel layout (riga di riferimento per percentuali).
  // Tutti i % vengono calcolati come (importo / ricavi_netti_col).
  const ricaviNettiRow = computedRows.find(r => r.tipo === "ricavi");
  const ricaviNettiPerCol = ricaviNettiRow ? ricaviNettiRow.values : {};
  const ricaviNettiTotal = ricaviNettiRow ? ricaviNettiRow.total : 0;

  const formatPct = (val, base) => {
    if (!base || base === 0 || val === 0 || val === undefined || val === null) return null;
    const pct = (val / base) * 100;
    return `${pct.toFixed(1)}%`;
  };

  // Controlla se un conto ha costi ripartiti
  const hasRipartizione = (contoNome) => {
    return costiViewMode === "ripartiti" && ripartizioneInfo && ripartizioneInfo[contoNome];
  };

  return (
    <>
      {computedRows.map((row, idx) => {
        if (row.tipo === "margine") {
          return (
            <React.Fragment key={`margine-${row.nome}-${idx}`}>
              <tr className="h-1" />
              <tr className="bg-blue-100 border-t-2 border-b-2 border-blue-300 font-bold" data-testid={`margine-row-${idx}`}>
                <td className="p-3 min-w-[250px] sticky left-0 bg-blue-100 z-10">
                  <div className="flex items-center gap-2">
                    <Equal className="w-4 h-4 text-blue-700" />
                    <span className="text-blue-900">{row.nome}</span>
                  </div>
                </td>
                {columnKeys.map(key => {
                  const val = row.values[key] || 0;
                  const ore = getOreColonna(key);
                  const pct = formatPct(val, ricaviNettiPerCol[key]);
                  return (
                    <td key={key} className={`p-3 text-right font-mono min-w-[100px] ${val >= 0 ? "text-blue-800" : "text-orange-700"}`}>
                      <div>{formatCurrency(val)}</div>
                      {showCostoOrario && ore > 0 && val !== 0 && (
                        <div className={`text-[11px] font-medium ${val >= 0 ? "text-blue-600" : "text-orange-600"}`}>{(val/ore).toFixed(2)} €/h</div>
                      )}
                      {pct && (
                        <div className={`text-[11px] font-medium ${val >= 0 ? "text-blue-500" : "text-orange-500"}`}>{pct}</div>
                      )}
                    </td>
                  );
                })}
                <td className={`p-3 text-right font-mono text-lg min-w-[120px] ${row.total >= 0 ? "bg-blue-200 text-blue-900" : "bg-orange-200 text-orange-900"}`}>
                  <div>{formatCurrency(row.total)}</div>
                  {showCostoOrario && oreTotaliPeriodo > 0 && row.total !== 0 && (
                    <div className={`text-[11px] font-medium ${row.total >= 0 ? "text-blue-700" : "text-orange-700"}`}>{(row.total/oreTotaliPeriodo).toFixed(2)} €/h</div>
                  )}
                  {formatPct(row.total, ricaviNettiTotal) && (
                    <div className={`text-[11px] font-medium ${row.total >= 0 ? "text-blue-600" : "text-orange-600"}`}>{formatPct(row.total, ricaviNettiTotal)}</div>
                  )}
                </td>
              </tr>
              <tr className="h-1" />
            </React.Fragment>
          );
        }

        const isRicavo = row.tipo === "ricavi";
        const isCosto = !isRicavo;
        const bgHeader = isRicavo ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200";
        const bgDetail = isRicavo ? "bg-green-50/30" : "bg-red-50/30";
        const textColor = isRicavo ? "text-green-800" : "text-red-800";
        const textColorLight = isRicavo ? "text-green-600" : "text-red-600";
        const isExpanded = expandedGroups.has(idx);

        return (
          <React.Fragment key={`group-${row.nome}-${idx}`}>
            <tr
              className={`${bgHeader} border-b cursor-pointer hover:opacity-80 transition-opacity`}
              onClick={() => toggleGroup(idx)}
              data-testid={`gruppo-row-${idx}`}
            >
              <td className={`p-2.5 pl-4 min-w-[250px] sticky left-0 z-10 ${bgHeader}`}>
                <div className="flex items-center gap-2 font-semibold">
                  <span className={textColor}>
                    {isExpanded ? <ChevronDown className="w-4 h-4 inline" /> : <ArrowDown className="w-4 h-4 rotate-0 inline" />}
                  </span>
                  <span className={textColor}>
                    {isRicavo ? <Plus className="w-3.5 h-3.5 inline" /> : <Minus className="w-3.5 h-3.5 inline" />}
                  </span>
                  <span className={textColor}>{row.nome}</span>
                  <Badge variant="outline" className="text-[10px] font-normal ml-1">
                    {(row.conti || []).length} conti
                  </Badge>
                  {/* Toggle Costi Originali/Ripartiti per gruppi costo in modalita CdC */}
                  {isCosto && columnMode === "cdc" && setCostiViewMode && (
                    <div className="flex gap-0.5 p-0.5 bg-red-100 rounded-md ml-auto" onClick={(e) => e.stopPropagation()} data-testid="scalare-costi-view-toggle">
                      <button
                        onClick={() => setCostiViewMode("originali")}
                        className={`px-2 py-0.5 text-[10px] font-medium rounded transition-colors ${costiViewMode === "originali" ? "bg-white text-red-800 shadow-sm" : "text-red-600 hover:text-red-800"}`}
                        data-testid="scalare-toggle-costi-originali"
                      >
                        Costi Originali
                      </button>
                      <button
                        onClick={() => setCostiViewMode("ripartiti")}
                        className={`px-2 py-0.5 text-[10px] font-medium rounded transition-colors ${costiViewMode === "ripartiti" ? "bg-white text-red-800 shadow-sm" : "text-red-600 hover:text-red-800"}`}
                        data-testid="scalare-toggle-costi-ripartiti"
                      >
                        Costi Ripartiti
                      </button>
                    </div>
                  )}
                </div>
              </td>
              {columnKeys.map(key => {
                const ore = getOreColonna(key);
                const v = row.values[key];
                const pct = formatPct(v, ricaviNettiPerCol[key]);
                return (
                  <td key={key} className={`p-2.5 text-right font-mono font-medium min-w-[100px] ${textColor}`}>
                    <div>{v ? formatCurrency(v) : "-"}</div>
                    {showCostoOrario && ore > 0 && v && (
                      <div className={`text-[11px] font-medium ${isRicavo ? "text-green-600" : "text-red-600"}`}>{(v/ore).toFixed(2)} €/h</div>
                    )}
                    {pct && (
                      <div className={`text-[11px] font-medium ${isRicavo ? "text-green-500" : "text-red-500"}`}>{pct}</div>
                    )}
                  </td>
                );
              })}
              <td className={`p-2.5 text-right font-mono font-bold min-w-[120px] ${bgHeader} ${textColor}`}>
                <div>{formatCurrency(row.total)}</div>
                {showCostoOrario && oreTotaliPeriodo > 0 && row.total > 0 && (
                  <div className={`text-[11px] font-medium ${isRicavo ? "text-green-600" : "text-red-600"}`}>{(row.total/oreTotaliPeriodo).toFixed(2)} €/h</div>
                )}
                {formatPct(row.total, ricaviNettiTotal) && (
                  <div className={`text-[11px] font-medium ${isRicavo ? "text-green-500" : "text-red-500"}`}>{formatPct(row.total, ricaviNettiTotal)}</div>
                )}
              </td>
            </tr>
            {/* Info ripartizione sotto il gruppo costo */}
            {isCosto && isExpanded && columnMode === "cdc" && (
              <>
                {costiViewMode === "ripartiti" && (
                  <tr className="bg-red-50/40">
                    <td colSpan={columnKeys.length + 2} className="px-4 py-1">
                      <p className="text-[10px] text-red-600/70 flex items-center gap-1">
                        <Info className="h-3 w-3" />
                        I costi dei CDC "da ripartire" sono redistribuiti sui CDC destinatari.
                      </p>
                    </td>
                  </tr>
                )}
                {costiViewMode === "originali" && (
                  <tr className="bg-blue-50/40">
                    <td colSpan={columnKeys.length + 2} className="px-4 py-1">
                      <p className="text-[10px] text-blue-600/70 flex items-center gap-1">
                        <Info className="h-3 w-3" />
                        Vista pre-ripartizione: i costi sono sul centro di costo originale.
                      </p>
                    </td>
                  </tr>
                )}
              </>
            )}
            {isExpanded && (row.conti || []).map((conto, ci) => {
              const contoHasRipartizione = isCosto && hasRipartizione(conto.nome);
              const canDrilldown = onCellClick && dettaglioMap;
              return (
                <tr key={`${conto.codice}-${ci}`} className={ci % 2 === 0 ? "bg-white" : bgDetail}>
                  <td className="p-2 pl-12 min-w-[250px] sticky left-0 bg-inherit z-10 text-sm">
                    <div className="flex items-center gap-1.5">
                      {conto.codice && <span className="text-muted-foreground">{conto.codice}</span>}
                      <span>{conto.nome}</span>
                      {contoHasRipartizione && (
                        <ArrowLeftRight className="h-3 w-3 text-amber-500 shrink-0 ml-1" title="Contiene costi ripartiti" />
                      )}
                    </div>
                  </td>
                  {columnKeys.map(key => {
                    const val = conto.values[key];
                    const ore = getOreColonna(key);
                    const hasDetail = canDrilldown && dettaglioMap[conto.nome]?.[key]?.length > 0;
                    const pct = formatPct(val, ricaviNettiPerCol[key]);
                    return (
                      <td key={key} 
                        className={`p-2 text-right font-mono text-sm min-w-[100px] ${textColorLight} ${hasDetail ? "cursor-pointer hover:underline hover:bg-slate-100 transition-colors" : ""}`}
                        onClick={() => hasDetail && onCellClick(conto.nome, conto.codice, key)}
                      >
                        <div>{val ? formatCurrency(val) : "-"}</div>
                        {showCostoOrario && ore > 0 && val && (
                          <div className={`text-[11px] font-medium opacity-70`}>{(val/ore).toFixed(2)} €/h</div>
                        )}
                        {pct && (
                          <div className={`text-[11px] font-medium opacity-60`}>{pct}</div>
                        )}
                      </td>
                    );
                  })}
                  <td className={`p-2 text-right font-mono text-sm min-w-[120px] ${bgDetail} ${textColorLight}`}>
                    {(() => {
                      const tot = columnKeys.reduce((s, k) => s + (conto.values[k] || 0), 0);
                      return (
                        <>
                          <div>{formatCurrency(tot)}</div>
                          {showCostoOrario && oreTotaliPeriodo > 0 && tot > 0 && (
                            <div className="text-[11px] font-medium opacity-70">{(tot/oreTotaliPeriodo).toFixed(2)} €/h</div>
                          )}
                          {formatPct(tot, ricaviNettiTotal) && (
                            <div className="text-[11px] font-medium opacity-60">{formatPct(tot, ricaviNettiTotal)}</div>
                          )}
                        </>
                      );
                    })()}
                  </td>
                </tr>
              );
            })}
          </React.Fragment>
        );
      })}
    </>
  );
}
